import { create } from 'zustand';
import {
  DEFAULT_PLAYLIST_SORT_DIRECTION,
  DEFAULT_PLAYLIST_SORT_KEY,
  PLAYLIST_SORT_KEYS,
} from '@/lib/constants/playlist';
import { GridSortStorage } from '@/shared/utils/gridSortStorage';
import type { PlaylistSortKey } from '@/lib/types/Playlist';
import type { SearchMode } from '@/lib/types/Media';
import type { SortDirection } from '@/lib/types/common';

const SORT_STORAGE_FEATURE = 'playlists';

function loadPersistedSort(): Pick<PlaylistGridState, 'sortKey' | 'sortDirection'> {
  const stored = GridSortStorage.load(SORT_STORAGE_FEATURE, PLAYLIST_SORT_KEYS, {
    key: DEFAULT_PLAYLIST_SORT_KEY,
    direction: DEFAULT_PLAYLIST_SORT_DIRECTION,
  });
  return { sortKey: stored.key, sortDirection: stored.direction };
}

interface PlaylistGridState {
  searchText: string;
  searchMode: SearchMode;
  sortKey: PlaylistSortKey;
  sortDirection: SortDirection;
  scrollTop: number;
  awaitingBackNavPaint: boolean;
  orderedIdsSnapshot: number[];
  orderedIdsSignature: string | null;
  orderedIdsQueryKey: string | null;
}

interface PlaylistGridActions {
  setSearchText: (text: string) => void;
  setSearchMode: (mode: SearchMode) => void;
  setSort: (key: PlaylistSortKey) => void;
  setScrollTop: (top: number) => void;
  setOrderedIdsSnapshot: (signature: string, queryKey: string, ids: number[]) => void;
  markBackNavStart: () => void;
  completeBackNavPaint: () => void;
  reset: () => void;
}

const initialState: PlaylistGridState = {
  searchText: '',
  searchMode: 'fuzzy',
  ...loadPersistedSort(),
  scrollTop: 0,
  awaitingBackNavPaint: false,
  orderedIdsSnapshot: [],
  orderedIdsSignature: null,
  orderedIdsQueryKey: null,
};

export const usePlaylistGridState = create<PlaylistGridState & PlaylistGridActions>()((set) => ({
  ...initialState,

  setSearchText: (text) => set({ searchText: text }),

  setSearchMode: (mode) => set({ searchMode: mode }),

  setSort: (key) =>
    set((state) => {
      const next: Pick<PlaylistGridState, 'sortKey' | 'sortDirection'> =
        state.sortKey === key
          ? { sortKey: key, sortDirection: state.sortDirection === 'asc' ? 'desc' : 'asc' }
          : { sortKey: key, sortDirection: 'desc' };
      GridSortStorage.save(SORT_STORAGE_FEATURE, next.sortKey, next.sortDirection);
      return next;
    }),

  setScrollTop: (top) =>
    set((state) => {
      if (Math.abs(state.scrollTop - top) < 1) return state;
      return { scrollTop: top };
    }),

  setOrderedIdsSnapshot: (signature, queryKey, ids) =>
    set((state) => {
      if (
        state.orderedIdsSignature === signature &&
        state.orderedIdsQueryKey === queryKey &&
        state.orderedIdsSnapshot === ids
      ) {
        return state;
      }

      return {
        orderedIdsSignature: signature,
        orderedIdsQueryKey: queryKey,
        orderedIdsSnapshot: ids,
      };
    }),

  markBackNavStart: () => set({ awaitingBackNavPaint: true }),

  completeBackNavPaint: () => set({ awaitingBackNavPaint: false }),

  // Sort survives reset: it's a persisted browser preference, not session state.
  reset: () => set({ ...initialState, ...loadPersistedSort() }),
}));
